import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChefHat } from "lucide-react";
import api from "../lib/api";
import { pinForTerm, pinUrl } from "../lib/pins";

export default function TermDetail() {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getEntry(slug);
        setEntry(data);
      } catch (e) { setNotFound(true); }
    })();
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="term-not-found">
      <div className="font-display italic text-4xl">Term not found.</div>
      <Link to="/glossary" className="btn-primary mt-6 inline-flex">Back to glossary</Link>
    </div>
  );
  if (!entry) return <div className="max-w-3xl mx-auto px-6 py-24 font-ui uppercase tracking-widest text-sm opacity-60" data-testid="term-loading">Loading…</div>;

  // Some terms have a matching branded Pinterest pin (30 curated so far).
  // When one exists, we surface it under the hero image so readers can
  // click through to /term/... from Pinterest and see the whole entry
  // sitting under a piece of pinnable art.
  const pinFile = pinForTerm(entry.slug);

  return (
    <article className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="term-detail">
      <Link to="/glossary" className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest opacity-70 hover-underline mb-10" data-testid="back-to-glossary">
        <ArrowLeft size={14} /> Back to glossary
      </Link>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        {/* Image column — sticky on desktop */}
        <div className="md:col-span-5">
          <div className="md:sticky md:top-24">
            <div className="aspect-[4/5] overflow-hidden border border-black/15">
              <img src={entry.image_url} alt={entry.term} className="w-full h-full object-cover" />
            </div>
            <div className="mt-4 flex items-center justify-between font-ui text-xs uppercase tracking-widest">
              <Link
                to={`/glossary?category=${encodeURIComponent(entry.category)}`}
                className="pill pill-mustard hover-underline"
                data-testid="term-category-link"
              >
                {entry.category}
              </Link>
              <span className="opacity-60">{entry.part_of_speech}</span>
            </div>

            {pinFile && (
              <div className="mt-6" data-testid="term-pin-card">
                <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">
                  Pinterest pin for this term
                </div>
                <a
                  href={pinUrl(pinFile)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-xl overflow-hidden border-2 hover:opacity-90 transition-opacity"
                  style={{ borderColor: "var(--olive, #5C6B46)" }}
                  data-testid="term-pin-link"
                  title="Open the full-size pin in a new tab"
                >
                  <img
                    src={pinUrl(pinFile)}
                    alt={`${entry.term} — Bite & Banter Pinterest pin`}
                    loading="lazy"
                    decoding="async"
                    className="block w-full h-auto"
                    style={{ aspectRatio: "2/3" }}
                  />
                </a>
                <div className="mt-2 font-ui text-[10px] uppercase tracking-widest opacity-60">
                  Right-click to save · share on Pinterest
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Text column */}
        <div className="md:col-span-7">
          <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-4">Entry</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.85] tracking-tight" data-testid="term-title">{entry.term}</h1>
          {entry.pronunciation && (
            <div className="font-body italic text-xl opacity-70 mt-3" data-testid="term-pronunciation">{entry.pronunciation}</div>
          )}

          {entry.formal_definition && (
            <div className="mt-8 border-l-4 pl-6" style={{ borderColor: "var(--olive)" }}>
              <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">Definition</div>
              <p className="font-body text-lg leading-relaxed" data-testid="term-formal">{entry.formal_definition}</p>
            </div>
          )}

          <div className="editorial-rule my-8" />

          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60 mb-3">In plain English</div>
          <p className="font-body text-lg leading-relaxed drop-cap" data-testid="term-definition">
            {entry.definition}
          </p>

          {entry.example && (
            <div className="mt-10">
              <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3">In a sentence</div>
              <blockquote className="font-display italic text-2xl md:text-3xl leading-snug border-l-4 pl-6" style={{ borderColor: "var(--tomato)" }} data-testid="term-example">
                &ldquo;{entry.example}&rdquo;
              </blockquote>
            </div>
          )}

          {entry.related_recipes?.length > 0 && (
            <div className="mt-14">
              <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-4 flex items-center gap-2">
                <ChefHat size={14} /> Try it in
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="related-recipes">
                {entry.related_recipes.map((r) => (
                  <Link key={r.slug} to={`/recipe/${r.slug}`} className="card-editorial flex" data-testid={`related-recipe-${r.slug}`}>
                    <div className="w-24 shrink-0 aspect-square overflow-hidden">
                      <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4 flex-1">
                      <div className="font-ui text-[10px] uppercase tracking-[0.25em] opacity-60">{r.cook_time}</div>
                      <div className="font-display text-xl leading-tight mt-1">{r.title}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-16 border-t border-black/15 pt-8">
            <p className="font-display italic text-xl md:text-2xl opacity-90" data-testid="term-closing-line">
              — {entry.closing_line}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}
